import { AccommodationCase, Accommodation } from '../../../../../data-models';
import { dateFormat } from './shared-styles';
import dayjs from 'dayjs';
import {
  TextBase,
  HeaderTextMedium,
  MobileContainerSM,
  DesktopContainerSM,
} from '../../../tw-components';
import { CardItemContainer } from '../../components/tw-components';

type AccommodationCardItemProps = {
  accommodationCase: AccommodationCase;
};

type AccommodationCardListProps = {
  cases: AccommodationCase[];
};

const getMappedEndDate = (accm: Accommodation) => {
  const decision = (accm?.decision ?? '').toLowerCase();
  const endDateStr = (accm?.endDate ?? '').toString().trim();

  if (decision === 'accommodated' && endDateStr === '') {
    return 'Ongoing';
  }

  if (endDateStr.includes('2150')) {
    return 'Ongoing';
  }

  if (!endDateStr) return '';

  const d = dayjs(endDateStr);
  return d.isValid() ? d.format(dateFormat) : '';
};

export const AccommodationCardList = ({
  cases,
}: AccommodationCardListProps) => {
  if (!cases.length) return null;

  return (
    <div className="tw-pb-2">
      {cases.map((ac) => (
        <AccommodationCardItem key={ac.caseId} accommodationCase={ac} />
      ))}
    </div>
  );
};

export const AccommodationCardItem = ({
  accommodationCase,
}: AccommodationCardItemProps) => {
  return (
    <div className="accm-card-container tw-w-full">
      {accommodationCase?.accommodations?.map((accm, i) => {
        const mappedRequest =
          accm?.accommodationType?.name?.trim() ??
          accm?.categoryName?.trim() ??
          '';
        const mappedEndDate = getMappedEndDate(accm);
        const showDates =
          (accm?.accommodationType?.name?.trim() !== 'reasonable work leave' ||
            accm?.categoryName?.trim() !== 'reasonable work leave') &&
          accm?.decision !== 'Pending';

        return (
          <div key={`accm-item-${accm?.accommodationType?.name}-${i}`}>
            <CardItemContainer>
              <div className="tw-flex tw-flex-col tw-w-full">
                {/* ------------------------------ Mobile View ----------------------------*/}
                <MobileContainerSM>
                  <div className="tw-flex tw-flex-col tw-w-full tw-gap-2 tw-justify-between tw-items-center">
                    <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
                      <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
                        <HeaderTextMedium>Request</HeaderTextMedium>
                        <TextBase>{mappedRequest}</TextBase>
                      </div>
                      <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
                        <HeaderTextMedium>Status</HeaderTextMedium>
                        <TextBase>{accm.decision}</TextBase>
                      </div>
                    </div>
                    {showDates && (
                      <>
                        {(accm?.implementedDate || mappedEndDate) && (
                          <div
                            className="tw-px-3 tw-my-2 tw-w-full
                                      tw-border-b tw-border-solid tw-border-unm-interactive01-400 tw-border-0"
                          ></div>
                        )}

                        <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
                          <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
                            {accm?.implementedDate && (
                              <>
                                <HeaderTextMedium>Start date</HeaderTextMedium>
                                <TextBase>
                                  {dayjs(accm.implementedDate).format(
                                    dateFormat,
                                  )}
                                </TextBase>
                              </>
                            )}
                          </div>
                          <div className="tw-flex tw-flex-col tw-w-1/2 tw-gap-2">
                            {mappedEndDate && (
                              <>
                                <HeaderTextMedium>End date</HeaderTextMedium>
                                <TextBase>{mappedEndDate}</TextBase>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </MobileContainerSM>

                {/* ------------------------------ Desktop View ------------------------------ */}
                <DesktopContainerSM className="tw-justify-between">
                  <div className="tw-flex tw-flex-row tw-w-full tw-gap-2">
                    <div className="tw-flex tw-flex-col tw-w-5/12 tw-gap-2">
                      <HeaderTextMedium>Request</HeaderTextMedium>
                      <TextBase>{mappedRequest}</TextBase>
                    </div>
                    <div className="tw-flex tw-flex-row tw-w-7/12 tw-gap-2">
                      {showDates && (
                        <>
                          <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
                            {accm?.implementedDate && (
                              <>
                                <HeaderTextMedium>Start date</HeaderTextMedium>
                                <TextBase>
                                  {dayjs(accm.implementedDate).format(
                                    dateFormat,
                                  )}
                                </TextBase>
                              </>
                            )}
                          </div>
                          <div className="tw-flex tw-flex-col tw-w-1/3 tw-gap-2">
                            {mappedEndDate && (
                              <>
                                <HeaderTextMedium>End date</HeaderTextMedium>
                                <TextBase>{mappedEndDate}</TextBase>
                              </>
                            )}
                          </div>
                        </>
                      )}
                      <div className="tw-flex tw-gap-2 tw-w-1/3">
                        <div className="tw-flex tw-flex-col tw-gap-2">
                          <HeaderTextMedium>Status</HeaderTextMedium>
                          <TextBase>{accm.decision}</TextBase>
                        </div>
                      </div>
                    </div>
                  </div>
                </DesktopContainerSM>
              </div>
            </CardItemContainer>
          </div>
        );
      })}
    </div>
  );
};

export default AccommodationCardList;
